<script setup lang="ts">
import type { ISchoolGradeDisplayConfiguration } from '@/core/school-grades/schoolGrade.types';
import { hasOverallAssessmentRating } from 'common/utils/performance.assessment.entry.utils';
import { PerformanceAssessmentDocumentationMode } from '@/api/generated/api.enum';
import AssessmentRatingInput from '@/core/performanceAssessments/AssessmentRatingInput.vue';
import { getAssessmentDocumentationMode } from 'common/utils/performance.assessment.utils';
import { Components } from '@/api/generated/api';
import CompetenceDomainIcon from '@/core/competences/CompetenceDomainIcon.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import CustomCheckbox from '@/core/elements/CustomCheckbox.vue';
import IconButton from '@/core/elements/IconButton.vue';
import SegmentedControl from '@/core/elements/SegmentedControl.vue';
import PerformanceLevelRatingInput from '@/core/performance-visuals/PerformanceLevelRatingInput.vue';
import TooltipTriangle from '@/core/tooltip/TooltipTriangle.vue';
import { ISelectOption } from '@/interfaces';
import { EIcon, EPosition } from 'common/enum/misc.enum';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type {
  ICollectiveAssessmentPayload,
  ICollectiveAssessmentRatingData,
  TCollectiveAssessmentTarget,
} from './performanceAssessmentCollectiveAssessment.types';

interface ICompetenceOption extends ISelectOption<number> {
  iconColor?: string;
  iconName?: string;
}

const emit = defineEmits<{
  apply: [payload: ICollectiveAssessmentPayload];
  close: [];
  'content-change': [];
}>();

const props = withDefaults(
  defineProps<{
    gradeConfiguration: ISchoolGradeDisplayConfiguration | null;
    availableCompetenceDomains: Components.Schemas.CompetenceDomainDto[];
    competenceAssessment: Components.Schemas.CompetenceAssessmentDto | null;
    disabled?: boolean;
    ratingData: ICollectiveAssessmentRatingData;
    position?: EPosition;
    variant?: 'default' | 'mobile';
  }>(),
  {
    disabled: false,
    position: EPosition.TOP_CENTER,
    variant: 'default',
  },
);

const { availableCompetenceDomains, competenceAssessment, disabled, ratingData } = toRefs(props);
const { t } = useI18n();

const selectedTarget = ref<TCollectiveAssessmentTarget>('performanceAssessment');
const selectedCompetenceIds = ref<number[]>([]);
const selectedRatingValue = ref<number | null>(null);
const ratingTouched = ref<boolean>(false);

const hasPerformanceAssessmentTarget = computed(() =>
  hasOverallAssessmentRating(ratingData.value.performanceAssessment),
);

const observedCompetenceIds = computed<Set<number>>(
  () => new Set(ratingData.value.competenceRatings.map((rating) => rating.competenceId)),
);

const competenceOptions = computed<ICompetenceOption[]>(() => {
  const options: ICompetenceOption[] = [];

  for (const domain of availableCompetenceDomains.value) {
    for (const competence of domain.competences) {
      if (!observedCompetenceIds.value.has(competence.id)) continue;
      options.push({
        iconColor: ratingData.value.schoolSubjectColor,
        iconName: domain.symbol,
        label: competence.description,
        value: competence.id,
      });
    }
  }

  const knownCompetenceIds = new Set(options.map((option) => option.value));
  for (const competenceId of observedCompetenceIds.value) {
    if (knownCompetenceIds.has(competenceId)) continue;
    options.push({
      iconColor: ratingData.value.schoolSubjectColor,
      iconName: EIcon.COMPETENCE2,
      label: t('performanceAssessmentEntry.collectiveAssessment.unknownCompetence', { id: competenceId }),
      value: competenceId,
    });
  }

  return options;
});

const hasCompetenceTarget = computed<boolean>(() => {
  return !!competenceAssessment.value && competenceOptions.value.length > 0;
});

const targetOptions = computed<ISelectOption<TCollectiveAssessmentTarget>[]>(() => {
  const options: ISelectOption<TCollectiveAssessmentTarget>[] = [];

  if (hasPerformanceAssessmentTarget.value) {
    options.push({
      label: t('performanceAssessmentEntry.collectiveAssessment.targetPerformanceAssessment'),
      value: 'performanceAssessment',
    });
  }

  if (hasCompetenceTarget.value) {
    options.push({
      label: t('performanceAssessmentEntry.collectiveAssessment.targetCompetences'),
      value: 'competence',
    });
  }

  return options;
});

const selectedAssessment = computed<
  Components.Schemas.CompetenceAssessmentDto | Components.Schemas.BasePerformanceAssessmentDto | null
>(() => {
  if (selectedTarget.value === 'competence') return competenceAssessment.value;
  return ratingData.value.performanceAssessment;
});

const isSchoolGradeTarget = computed(
  () =>
    selectedTarget.value === 'performanceAssessment' &&
    getAssessmentDocumentationMode(ratingData.value.performanceAssessment) ===
      PerformanceAssessmentDocumentationMode.SCHOOL_GRADE,
);

const selectedTargetValues = computed<(number | null)[]>(() => {
  if (selectedTarget.value === 'competence') {
    const competenceIds = new Set(selectedCompetenceIds.value);
    return ratingData.value.competenceRatings
      .filter((rating) => competenceIds.has(rating.competenceId))
      .map((rating) => rating.performanceLevelV2Id);
  }
  return ratingData.value.overallRatings.map((rating) =>
    isSchoolGradeTarget.value ? (rating.gradeValue ?? null) : rating.performanceLevelV2Id,
  );
});
const initialRatingValue = computed<number | null>(() => {
  const values = new Set(selectedTargetValues.value);
  return values.size === 1 ? (Array.from(values)[0] ?? null) : null;
});

// a single connected competence needs no picker — the same rule the target tabs use
const showCompetenceList = computed<boolean>(() => competenceOptions.value.length > 1);

const canApply = computed<boolean>(() => {
  if (!ratingTouched.value) return false;
  if (isSchoolGradeTarget.value && selectedRatingValue.value == null) return false;
  if (disabled.value || !selectedAssessment.value) return false;
  if (selectedTarget.value === 'competence' && !selectedCompetenceIds.value.length) return false;
  // applying is only meaningful when at least one selected item would actually change; comparing
  // against initialRatingValue would treat a mixed selection like an unrated one
  return selectedTargetValues.value.some((value) => value !== selectedRatingValue.value);
});

const resetSelectionState = (): void => {
  selectedRatingValue.value = initialRatingValue.value;
  ratingTouched.value = false;
};

const resetTargetIfInvalid = (): void => {
  if (targetOptions.value.some((option) => option.value === selectedTarget.value)) return;
  selectedTarget.value = targetOptions.value[0]?.value ?? 'performanceAssessment';
};

const syncCompetenceSelection = (): void => {
  // a hidden list offers no way to pick, so its sole competence stays selected
  if (!showCompetenceList.value) {
    selectedCompetenceIds.value = competenceOptions.value.map((option) => option.value);
    return;
  }
  const available = new Set(competenceOptions.value.map((option) => option.value));
  const remaining = selectedCompetenceIds.value.filter((id) => available.has(id));
  if (remaining.length !== selectedCompetenceIds.value.length) selectedCompetenceIds.value = remaining;
};

const isCompetenceSelected = (competenceId: number): boolean => selectedCompetenceIds.value.includes(competenceId);

const toggleCompetence = (competenceId: number): void => {
  selectedCompetenceIds.value = isCompetenceSelected(competenceId)
    ? selectedCompetenceIds.value.filter((id) => id !== competenceId)
    : [...selectedCompetenceIds.value, competenceId];
};

const handleRatingChange = (value: number | null): void => {
  selectedRatingValue.value = selectedRatingValue.value === value ? null : value;
  ratingTouched.value = true;
};

const prepare = (): void => {
  resetTargetIfInvalid();
  // every open starts from the first competence, whether or not the list is shown
  selectedCompetenceIds.value = competenceOptions.value.slice(0, 1).map((option) => option.value);
  resetSelectionState();
};

const handleApply = (): void => {
  if (!canApply.value) return;

  emit('apply', {
    competenceIds: selectedTarget.value === 'competence' ? [...selectedCompetenceIds.value] : [],
    performanceLevelV2Id: isSchoolGradeTarget.value ? null : selectedRatingValue.value,
    ...(isSchoolGradeTarget.value ? { gradeValue: selectedRatingValue.value } : {}),
    target: selectedTarget.value,
  });
};

watch(targetOptions, () => {
  resetTargetIfInvalid();
});

watch(competenceOptions, () => {
  syncCompetenceSelection();
});

// switching target swaps the performance visual, so a picked level no longer applies
watch(selectedTarget, () => {
  resetSelectionState();
  emit('content-change');
});

// every competence shares the competence assessment's visual, so an already picked level survives
// a selection change — otherwise picking the tile before the competence would clear the rating
watch(selectedCompetenceIds, () => {
  if (!ratingTouched.value) resetSelectionState();
  emit('content-change');
});

defineExpose({
  prepare,
});
</script>

<template>
  <div
    :class="[
      variant === 'mobile'
        ? 'fixed inset-x-0 bottom-0 flex max-h-mobile-drawer-tall flex-col gap-lg overflow-y-auto rounded-t-dialog bg-white px-lg pb-[calc(40px+env(safe-area-inset-bottom))] pt-lg shadow-popover'
        : 'relative flex max-h-[inherit] w-[368px] max-w-[calc(100vw-32px)] flex-col overflow-visible rounded-dialog bg-white shadow-floating-panel',
    ]"
  >
    <TooltipTriangle
      v-if="variant === 'default'"
      :class="[
        'pointer-events-none absolute left-1/2 h-icon-md w-button -translate-x-1/2 text-white drop-shadow-floating-panel-arrow',
        position === EPosition.TOP_CENTER && 'bottom-[-22px]',
        position === EPosition.BOTTOM_CENTER && 'top-[-22px] rotate-180',
      ]"
    />

    <div
      :class="variant === 'default' ? 'flex min-h-0 max-h-[inherit] flex-col gap-lg overflow-y-auto p-lg' : 'contents'"
    >
      <header v-if="variant === 'mobile'" class="flex shrink-0 items-center gap-md">
        <h2 class="min-w-0 flex-1 truncate text-title text-primary">
          {{ $t('performanceAssessmentEntry.collectiveAssessment.title') }}
        </h2>
        <IconButton
          :aria-label="$t('general.close')"
          :icon-name="EIcon.X"
          size="button"
          theme="secondary-filled"
          @click="$emit('close')"
        />
      </header>

      <SegmentedControl
        v-if="targetOptions.length > 1"
        v-model="selectedTarget"
        width="full"
        :aria-label="$t('performanceAssessmentEntry.collectiveAssessment.targetLabel')"
        :options="targetOptions"
      >
        <template #option-performanceAssessment="{ option }">
          <span class="block max-w-full min-w-0 truncate">{{ option.label }}</span>
        </template>
        <template #option-competence="{ option }">
          <span class="block max-w-full min-w-0 truncate">{{ option.label }}</span>
        </template>
      </SegmentedControl>

      <div
        v-if="selectedTarget === 'competence' && showCompetenceList"
        class="flex w-full shrink-0 flex-col gap-px"
        role="group"
        :aria-label="$t('performanceAssessmentEntry.collectiveAssessment.competenceLabel')"
      >
        <div
          v-for="option in competenceOptions"
          :key="option.value"
          :class="[
            'flex w-full shrink-0 cursor-pointer items-center gap-md overflow-hidden rounded-md px-md py-sm text-left outline-inner transition duration-300 state-effects',
            isCompetenceSelected(option.value) ? 'bg-highlight' : '',
          ]"
          role="checkbox"
          tabindex="0"
          :aria-checked="isCompetenceSelected(option.value)"
          @click="toggleCompetence(option.value)"
          @keydown.enter.stop.prevent="toggleCompetence(option.value)"
          @keydown.space.stop.prevent="toggleCompetence(option.value)"
        >
          <CustomCheckbox
            class="pointer-events-none"
            aria-hidden="true"
            readonly
            :model-value="isCompetenceSelected(option.value)"
          />
          <CompetenceDomainIcon
            v-if="option.iconName"
            :icon-name="option.iconName"
            :color="option.iconColor"
            class="pointer-events-none"
          />
          <span
            class="min-w-0 flex-1 truncate text-base text-primary"
            :class="isCompetenceSelected(option.value) ? 'text-tint font-semibold' : ''"
          >
            {{ option.label }}
          </span>
        </div>
      </div>

      <p class="text-footer leading-[160%] tracking-[0.12px] text-secondary">
        {{ $t('performanceAssessmentEntry.collectiveAssessment.hint') }}
      </p>

      <AssessmentRatingInput
        v-if="selectedTarget === 'performanceAssessment' && ratingData.performanceAssessment"
        :key="ratingData.performanceAssessment.id"
        :grade-configuration="gradeConfiguration"
        :assessment="ratingData.performanceAssessment"
        :accessible-label="$t('performanceAssessmentEntry.collectiveAssessment.title')"
        :disabled="disabled"
        mode="inline-cards"
        :model-value="selectedRatingValue"
        @update:model-value="
          selectedRatingValue = $event;
          ratingTouched = true;
        "
      />
      <PerformanceLevelRatingInput
        v-else-if="selectedAssessment"
        :accessible-label="$t('performanceAssessmentEntry.collectiveAssessment.title')"
        :disabled="disabled"
        mode="inline-cards"
        :model-value="selectedRatingValue"
        :performance-visual="selectedAssessment.performanceVisual"
        @update:model-value="handleRatingChange"
      />

      <CustomButton class="w-full justify-center" :disabled="!canApply" @click="handleApply">
        {{ $t('performanceAssessmentEntry.collectiveAssessment.apply') }}
      </CustomButton>
    </div>
  </div>
</template>
